// src/telemetry/frame_metrics.ts
// Frame metrics — tracks frame pipeline performance.
//
// Metrics collected:
//   - fps: instantaneous frame rate
//   - frames_received: total frames ingested
//   - frames_dropped: frames skipped (by backend compression)
//   - avg_frame_size: average serialized frame size in bytes
//   - replay_seek_latency: time to seek to a frame in ms

import { EventEmitter } from 'eventemitter3';

export interface FrameMetricsSnapshot {
  fps: number;
  framesReceived: number;
  framesDropped: number;
  avgFrameSizeBytes: number;
  replaySeekLatencyMs: number;
  totalFramesInCache: number;
  lastFrameTs: number;
  elapsedMs: number;
}

export class FrameMetrics extends EventEmitter {
  private framesReceived = 0;
  private framesDropped = 0;
  private totalFrameSizeBytes = 0;
  private fpsWindow: number[] = [];
  private fpsWindowSize = 100;
  private seekLatencies: number[] = [];
  private startTime = Date.now();

  constructor() {
    super();
  }

  onFrame(frameSizeBytes: number): void {
    this.framesReceived++;
    this.totalFrameSizeBytes += frameSizeBytes;

    const now = Date.now();
    this.fpsWindow.push(now);
    if (this.fpsWindow.length > this.fpsWindowSize) {
      this.fpsWindow.shift();
    }

    this.emit('frame', this.getSnapshot());
  }

  onDropped(): void {
    this.framesDropped++;
    this.emit('frame', this.getSnapshot());
  }

  onSeekStart(): number {
    return Date.now();
  }

  onSeekEnd(startTime: number): void {
    const latency = Date.now() - startTime;
    this.seekLatencies.push(latency);
    if (this.seekLatencies.length > 50) {
      this.seekLatencies.shift();
    }
    this.emit('seek', this.getSnapshot());
  }

  reset(): void {
    this.framesReceived = 0;
    this.framesDropped = 0;
    this.totalFrameSizeBytes = 0;
    this.fpsWindow = [];
    this.seekLatencies = [];
    this.startTime = Date.now();
    this.emit('reset', this.getSnapshot());
  }

  getSnapshot(): FrameMetricsSnapshot {
    const now = Date.now();
    const elapsedMs = now - this.startTime;

    let fps = 0;
    if (this.fpsWindow.length >= 2) {
      const windowDuration = now - this.fpsWindow[0];
      if (windowDuration > 0) {
        fps = ((this.fpsWindow.length - 1) / windowDuration) * 1000;
      }
    }

    const avgSeekLatency = this.seekLatencies.length > 0
      ? this.seekLatencies.reduce((a, b) => a + b, 0) / this.seekLatencies.length
      : 0;

    return {
      fps: Math.round(fps * 100) / 100,
      framesReceived: this.framesReceived,
      framesDropped: this.framesDropped,
      avgFrameSizeBytes: this.framesReceived > 0
        ? Math.round(this.totalFrameSizeBytes / this.framesReceived)
        : 0,
      replaySeekLatencyMs: Math.round(avgSeekLatency * 100) / 100,
      totalFramesInCache: 0,
      lastFrameTs: this.fpsWindow[this.fpsWindow.length - 1] ?? 0,
      elapsedMs,
    };
  }
}
